import { useState, useEffect } from 'react'
import { Card, Button, Input, Modal, Table, Loading, Badge, Alert, Select } from '../components/common'
import { masterService } from '../services/masterService'
import { Trash2 } from 'lucide-react'

function VendorsPage() {
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [formData, setFormData] = useState({ name: '', type: '', address: '', phone: '', pic: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, name }

  useEffect(() => { fetchData() }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await masterService.getVendors()
      if (response.success) setVendors(response.data.vendors || [])
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.name || !formData.type) { setError('Nama dan tipe layanan harus diisi'); return }
    setSaving(true)
    setError('')
    try {
      const response = editing
        ? await masterService.updateVendor(editing.id, formData)
        : await masterService.createVendor(formData)
      if (response.success) { setShowModal(false); setEditing(null); fetchData() }
      else { setError(response.message || 'Gagal menyimpan') }
    } catch (err) { setError(err.response?.data?.message || 'Gagal menyimpan') }
    finally { setSaving(false) }
  }

  const handleEdit = (item) => {
    setEditing(item)
    setFormData({ 
      name: item.name || '', 
      type: item.type || '', 
      address: item.address || '', 
      phone: item.phone || '', 
      pic: item.pic || '' 
    })
    setError('')
    setShowModal(true)
  }

  const handleDeleteClick = (vendor) => {
    if (vendor.id === 'internal') {
      alert('Vendor internal tidak dapat dihapus')
      return
    }
    setDeleteConfirm({ id: vendor.id, name: vendor.name })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return
    try {
      await masterService.deleteVendor(deleteConfirm.id)
      setDeleteConfirm(null)
      fetchData()
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus')
      setDeleteConfirm(null)
    }
  }

  const openAddModal = () => {
    setEditing(null)
    setFormData({ name: '', type: '', address: '', phone: '', pic: '' })
    setError('')
    setShowModal(true)
  }

  const columns = [
    { header: 'Kode', render: (row) => <span className="font-mono text-sm">{row.id}</span> },
    { header: 'Nama', render: (row) => <span className="font-medium">{row.name}</span> },
    { header: 'Tipe', render: (row) => <Badge variant={row.type === 'internal' ? 'default' : 'info'}>{row.type === 'internal' ? 'Internal' : 'Makloon'}</Badge> },
    { header: 'Alamat', render: (row) => row.address || '-' },
    { header: 'PIC', render: (row) => row.pic || '-' },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'active' ? 'success' : 'default'}>{row.status}</Badge> },
    { header: 'Aksi', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline text-sm">Edit</button>
        {row.id !== 'internal' && (
          <button onClick={() => handleDeleteClick(row)} className="text-red-600 hover:underline text-sm">Hapus</button>
        )}
      </div>
    )}
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">🏭 Master Vendor</h1>
          <p className="text-gray-500">Kelola data vendor eksternal</p>
        </div>
        <Button onClick={openAddModal}>➕ Tambah Vendor</Button>
      </div>

      <Card>
        {loading ? <Loading /> : <Table columns={columns} data={vendors} emptyText="Belum ada vendor" />}
      </Card>

      <Modal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={editing ? 'Edit Vendor' : 'Tambah Vendor'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Batal</Button>
            <Button onClick={handleSubmit} loading={saving}>Simpan</Button>
          </>
        }
      >
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Nama Vendor *" 
            value={formData.name} 
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} 
            placeholder="CV. Mitra Cetak Jaya"
          />
          <Select 
            label="Tipe Vendor *" 
            value={formData.type} 
            onChange={(e) => setFormData(p => ({ ...p, type: e.target.value }))}
            options={[
              { value: '', label: '-- Pilih Tipe --' }, 
              { value: 'makloon', label: 'Makloon (Vendor Eksternal)' }
            ]} 
          />
          <Input 
            label="Alamat" 
            value={formData.address} 
            onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} 
            placeholder="Jl. Industri No. 123"
          />
          <Input 
            label="Telepon" 
            value={formData.phone} 
            onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} 
            placeholder="021-12345678"
          />
          <Input 
            label="PIC (Person In Charge)" 
            value={formData.pic} 
            onChange={(e) => setFormData(p => ({ ...p, pic: e.target.value }))} 
            placeholder="Pak Joko"
          />
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hapus Vendor?</h3>
              <p className="text-gray-600 mb-1">
                Anda yakin ingin menghapus vendor:
              </p>
              <p className="font-medium text-gray-900 mb-4">"{deleteConfirm.name}"</p>
              <p className="text-sm text-red-600 mb-6">
                Tindakan ini tidak dapat dibatalkan.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  Ya, Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorsPage
